import { MouseEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  elementTypesWithStoreyWiseSelectability,
  DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG,
} from '@constants'
import { elementTypeShortLabel } from '@domainConstants'
import { sha256 } from 'js-sha256'
import { merge, reject, reverse, sortBy, toNumber } from 'lodash-es'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import PaletteIcon from '@mui/icons-material/Palette'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import {
  Box,
  Button,
  Checkbox,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material'
import { deepOrange, green, orange, purple } from '@mui/material/colors'
import useExtraVisibilityFilters from '@editor/components/Controls/VisibilityFilters/useExtraVisibilityFilters'
import { closedWidth as navRailClosedWidth } from '@editor/components/ProcessNavigation/styles'
import { useElementTypeSelection, useSetModelColumnCompact, useStoreyImages } from '@editor/hooks'
import useRegisterSnackbarAnchor from '@editor/hooks/useRegisterSnackbarAnchor'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useSceneStore } from '@editor/stores'
import { useCameraStore } from '@modugen/ui-toolkit'
import { TooltipIconButton } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { CollapsibleVerticalDivider } from '@ui/structure'
import { refusePaneChange, roofStoreyKey, useControlStore, useModelStore } from '@editorStores'
import { StoreyImageUpload } from '@editorComponents'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import CommentPlanFilterSelect from './CommentPlanFilterSelect'
import IFCStoreyControls from './IFCStoreyControls'
import ModelSettingsPopover from './ModelSettingsPopover'
import StoreyControlsTable from './StoreyControlsTable'
import StoreyControlsTableRow from './StoreyControlsTableRow'
import StoreyTypeToggleButton from './StoreyTypeToggleButton'
import WeiteresFilterPopover from './WeiteresFilterPopover'

interface StoreyControlsProps {
  /** Current selected storey (from query params or controlStore) */
  storey: string | null
  /** Function to set the selected storey */
  setStorey: (storey: string | null) => void
}

const storeyWallColorOptions: { label: string; value?: string }[] = [
  { label: 'Standardfarbe', value: undefined },
  { label: 'Schwarz', value: '#000000' },
  { label: 'Blau', value: '#0000FF' },
  { label: 'Grün', value: '#00FF00' },
  { label: 'Grün 2', value: green[500] },
  { label: 'Rot', value: '#FF0000' },
  { label: 'Gelb', value: '#FFFF00' },
  { label: 'Magenta', value: '#FF00FF' },
  { label: 'Cyan', value: '#00FFFF' },
  { label: 'Orange', value: '#FF8000' },
  { label: 'Orange 2', value: orange[700] },
  { label: 'Orange 3', value: deepOrange[900] },
  { label: 'Lila', value: purple[500] },
]

const storeyWallLinewidthOptions: { label: string; value?: number }[] = [
  { label: 'Standardbreite', value: undefined },
  ...Array.from({ length: 6 }, (_, index) => {
    const width = index * 1.0 + 1
    return {
      label: width.toFixed(1).replace('.', ','),
      value: width,
    }
  }),
]

const AWLabel = 'AW'
const IWLabel = 'IW'
const DEPosLabel = 'DE-Pos'
const BeamLabel = elementTypeShortLabel.beams ?? 'UZ'
const ColumnLabel = elementTypeShortLabel.columns ?? 'C'
const RipLabel = elementTypeShortLabel.rips ?? 'WS'
const LintelLabel = elementTypeShortLabel.lintels ?? 'FS'
const PurlinLabel = elementTypeShortLabel.purlins ?? 'P'
const StiffeningLabel = 'Wandscheiben'

const StoreyControls = ({ storey, setStorey }: StoreyControlsProps): ReactElement => {
  const theme = useTheme()

  const {
    model,
    visibleStoreys,
    availableStoreys,
    visibilityByType,
    visibilityByTypeAndStorey,
    toggleSingleStoreyVisibility,
    setVisibleStoreys,
    setAllStoreysInvisible,
    toggleTypeVisibilityByStorey,
    toggleTypeVisibility,
    showWallOpenings,
    setShowWallOpenings,
    showVerticalOpeningAlignmentHelpers,
    setShowVerticalOpeningAlignmentHelpers,
  } = useModelStore(state => state)
  const walls = useModelStore(state => state.model.walls)
  const modelColumnCompact = useControlStore(state => state.modelColumnCompact)
  const setModelColumnCompact = useSetModelColumnCompact()
  const coloringMode = useControlStore(state => state.coloringMode)
  const setColoringMode = useControlStore(state => state.setColoringMode)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const setStoreyControlsPortalElement = useControlStore(
    state => state.setStoreyControlsPortalElement,
  )
  const portalRef = useRef<HTMLDivElement | null>(null)
  const snackbarAnchorRef = useRegisterSnackbarAnchor<HTMLDivElement>()

  const manualGuidToColor = useSceneStore(state => state.manualGuidToColor)
  const setManualGuidToColorMapping = useSceneStore(state => state.setManualGuidToColorMapping)
  const storeyToWallLinewidth = useSceneStore(state => state.storeyToWallLinewidth)
  const setWallLinewidthForStorey = useSceneStore(state => state.setWallLinewidthForStorey)

  const visibility = useControlStore(state => state.structuralPlanningElementVisibility)
  const changeVisibility = useControlStore(state => state.changeStructuralPlanningElementVisibility)

  const isTypeVisibleForStorey = useCallback(
    (type: keyof typeof visibilityByTypeAndStorey, storey: string) =>
      visibilityByTypeAndStorey[type]?.has(storey) ?? true,
    [visibilityByTypeAndStorey],
  )

  const availableNonRoofStoreys = sortBy(
    reject(Array.from(availableStoreys), s => s === roofStoreyKey),
    s => toNumber(s),
  )

  const storeyToSelectedWallColor = useMemo<Record<string, string | undefined>>(
    () =>
      Object.fromEntries(
        availableNonRoofStoreys.map(storeyKey => {
          const storeyWalls = walls.filter(wall => wall.storey === storeyKey)
          const selectedColors = [
            ...new Set(
              storeyWalls
                .map(wall => manualGuidToColor[wall.guid])
                .filter((color): color is string => Boolean(color)),
            ),
          ]

          const color = selectedColors.length === 1 ? selectedColors[0] : undefined
          return [storeyKey, color]
        }),
      ),
    [availableNonRoofStoreys, manualGuidToColor, walls],
  )
  const storeyRowsRenderVersion = useMemo(
    () => sha256(JSON.stringify(model.storey_boundaries)),
    [model.storey_boundaries],
  )

  const {
    toggleStoreyImageVisibilityIn2D,
    toggleStoreyImageVisibilityIn3D,
    setStoreyImage,
    removeStoreyImage,
    updateStoreyImageTransform,
    storeyImages,
    isUploadingStoreyImage,
    isRemovingStoreyImage,
  } = useStoreyImages()
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const [storeyWallColorAnchorEl, setStoreyWallColorAnchorEl] = useState<null | HTMLElement>(null)
  const [storeyWithColorMenu, setStoreyWithColorMenu] = useState<string | null>(null)
  const [modelSettingsAnchorEl, setModelSettingsAnchorEl] = useState<null | HTMLElement>(null)
  const [weiteresFilterAnchorEl, setWeiteresFilterAnchorEl] = useState<null | HTMLElement>(null)
  const { activeCount: activeExtraFilterCount, toggleAll: toggleAllExtraFilters } =
    useExtraVisibilityFilters()
  const isStoreyWallColorMenuOpen = Boolean(storeyWallColorAnchorEl)

  // Image upload handlers
  const handleImageUpload = useCallback(
    (file: File, storeyKey: string, options?: { pdfPage?: number }) => {
      const existingImage = storeyImages[storeyKey]

      const data = merge({}, existingImage, DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG)

      setStoreyImage(storeyKey, file, data, options?.pdfPage)
    },
    [setStoreyImage, storeyImages],
  )

  const handleImageRemove = useCallback(
    (storeyKey: string) => {
      removeStoreyImage(storeyKey)
    },
    [removeStoreyImage],
  )

  const handleTransformUpdate = useCallback(
    (storeyKey: string, transform: StoreyImageTransform) => {
      updateStoreyImageTransform(storeyKey, transform)
    },
    [updateStoreyImageTransform],
  )

  const { selectElementsByType, selectElementsByTypeAndStorey } = useElementTypeSelection()
  const canSelectByStorey = (elementType: ElementTypes) =>
    elementTypesWithStoreyWiseSelectability.includes(elementType)

  const openStoreyWallColorMenu = useCallback(
    (event: MouseEvent<HTMLElement>, targetStorey: string) => {
      event.stopPropagation()
      setStoreyWithColorMenu(targetStorey)
      setStoreyWallColorAnchorEl(event.currentTarget)
    },
    [],
  )

  const closeStoreyWallColorMenu = useCallback(() => {
    setStoreyWallColorAnchorEl(null)
    setStoreyWithColorMenu(null)
  }, [])

  useEffect(() => {
    if (!portalRef.current) return
    setStoreyControlsPortalElement(portalRef.current)
  }, [setStoreyControlsPortalElement])

  const handleStoreyWallColorChange = useCallback(
    (color?: string) => {
      if (!storeyWithColorMenu) return

      const storeyWallGuids = walls
        .filter(wall => wall.storey === storeyWithColorMenu)
        .map(wall => wall.guid)

      const nextManualGuidToColor = { ...manualGuidToColor }
      storeyWallGuids.forEach(guid => {
        if (color) {
          nextManualGuidToColor[guid] = color
        } else {
          delete nextManualGuidToColor[guid]
        }
      })

      setManualGuidToColorMapping(nextManualGuidToColor)
      closeStoreyWallColorMenu()

      // The per-storey wall color only shows in the scene while the "Stockwerk" coloring mode is
      // active. If the user picks a color in another mode, warn them and offer to switch.
      if (coloringMode !== 'storey') {
        enqueueSnackbar('Die Stockwerksfarbe wird nur in der Einfärbung „Stockwerk“ angezeigt.', {
          variant: 'warning',
          action: key => (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setColoringMode('storey')
                closeSnackbar(key)
              }}
            >
              Zu „Stockwerk“ wechseln
            </Button>
          ),
        })
      }
    },
    [
      closeStoreyWallColorMenu,
      coloringMode,
      setColoringMode,
      enqueueSnackbar,
      closeSnackbar,
      manualGuidToColor,
      setManualGuidToColorMapping,
      storeyWithColorMenu,
      walls,
    ],
  )

  const handleStoreyWallLinewidthChange = useCallback(
    (linewidth?: number) => {
      if (!storeyWithColorMenu) return

      setWallLinewidthForStorey(storeyWithColorMenu, linewidth)
      closeStoreyWallColorMenu()
    },
    [closeStoreyWallColorMenu, setWallLinewidthForStorey, storeyWithColorMenu],
  )

  const showGltfModel = useControlStore(state => state.showGltfModel)

  // Whole-model visibility eye toggle (mirrors the IFC column). Remember the visible storeys before
  // hiding all, so toggling back restores the previous per-storey selection instead of showing all.
  const [storeysBeforeHideAll, setStoreysBeforeHideAll] = useState<string[] | null>(null)
  const isModelHidden = visibleStoreys.size === 0
  const toggleModelVisibility = useCallback(() => {
    if (isModelHidden) {
      setVisibleStoreys(storeysBeforeHideAll ?? Array.from(availableStoreys))
      setStoreysBeforeHideAll(null)
    } else {
      setStoreysBeforeHideAll(Array.from(visibleStoreys))
      setAllStoreysInvisible()
    }
  }, [
    isModelHidden,
    storeysBeforeHideAll,
    availableStoreys,
    visibleStoreys,
    setVisibleStoreys,
    setAllStoreysInvisible,
  ])

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Box
        component="div"
        sx={{ pointerEvents: 'auto', userSelect: 'none' }}
        borderLeft={1}
        borderBottom={1}
        borderColor="divider"
        bgcolor="white"
      >
        <CollapsibleVerticalDivider text="Filter" defaultExpanded={true}>
          <Box
            sx={{
              // The panel is anchored to the right edge of the scene pane and the table has a
              // fixed minimum width, so on narrow panes it would otherwise grow off-screen to
              // the left and become unreachable. Cap it to the pane width (the SceneControlled
              // query container) and scroll horizontally. `cqw` resolves to that container, not
              // the full viewport, so scrolling kicks in as soon as the panel fills the pane.
              // The reserved amount is exactly the left nav rail width (which overlays the scene
              // pane), so a wide panel grows left up to the rail and sits flush against it.
              maxWidth: `calc(100cqw - ${navRailClosedWidth}px - 28px)`,

              overflowX: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box mb={showGltfModel ? 0.7 : undefined} mt={0.5}>
                  <Stack direction="row" alignItems="center" sx={{ px: 0.5, py: 0.125 }}>
                    <Typography fontSize="small">Modell</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TooltipIconButton
                        size="small"
                        onClick={event => setModelSettingsAnchorEl(event.currentTarget)}
                        sx={{ p: 0, color: theme.palette.grey[700] }}
                        tooltip="Einstellungen"
                        data-cy="btn-model-settings"
                      >
                        <TuneIcon fontSize="small" />
                      </TooltipIconButton>
                      <TooltipIconButton
                        size="small"
                        onClick={() => setModelColumnCompact(!modelColumnCompact)}
                        sx={{ p: 0, color: theme.palette.grey[700] }}
                        tooltip={
                          modelColumnCompact ? 'Alle Schaltflächen anzeigen' : 'Kompakte Ansicht'
                        }
                        data-cy="btn-model-column-compact-toggle"
                      >
                        {modelColumnCompact ? (
                          <ZoomOutMapIcon fontSize="small" />
                        ) : (
                          <ZoomInMapIcon fontSize="small" />
                        )}
                      </TooltipIconButton>
                      <TooltipIconButton
                        size="small"
                        onClick={toggleModelVisibility}
                        sx={{ p: 0, color: theme.palette.grey[700] }}
                        tooltip={isModelHidden ? 'Modell anzeigen' : 'Modell ausblenden'}
                        data-cy="btn-model-show-toggle"
                      >
                        {isModelHidden ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </TooltipIconButton>
                    </Stack>
                  </Stack>
                </Box>
                <StoreyControlsTable>
                  <StoreyControlsTableRow
                    icon={
                      visibleStoreys.has(roofStoreyKey) ? (
                        <VisibilityIcon
                          fontSize="small"
                          sx={{ cursor: 'pointer', color: theme.palette.grey[700] }}
                          onClick={() => toggleSingleStoreyVisibility(roofStoreyKey)}
                          data-cy="btn-storey-roof-visibility"
                        />
                      ) : (
                        <VisibilityOffIcon
                          fontSize="small"
                          sx={{ cursor: 'pointer', color: theme.palette.grey[700] }}
                          onClick={() => toggleSingleStoreyVisibility(roofStoreyKey)}
                          data-cy="btn-storey-roof-visibility"
                        />
                      )
                    }
                    text={
                      <Typography fontSize="small" noWrap>
                        Dach
                      </Typography>
                    }
                    compact={modelColumnCompact}
                    columnsCombined={
                      <>
                        {!modelColumnCompact && (
                          <StoreyTypeToggleButton
                            value="vertical_roof_slabs"
                            label="DA-Pos"
                            selected={visibilityByType.vertical_roof_slabs}
                            onToggle={() => toggleTypeVisibility('vertical_roof_slabs')}
                            onSelect={event =>
                              selectElementsByTypeAndStorey('vertical_roof_slabs', roofStoreyKey, {
                                extend: eventMatchesPointerModifier(event, 'multiSelect'),
                              })
                            }
                            dataCy="btn-vertical-roof-slabs-visibility"
                          />
                        )}
                        <StoreyTypeToggleButton
                          value="roof_slabs"
                          label="DA-Fläche"
                          selected={visibilityByType.roof_slabs}
                          onToggle={() => toggleTypeVisibility('roof_slabs')}
                          dataCy="btn-roof-slabs-visibility"
                        />
                        {!modelColumnCompact && (
                          <>
                            <StoreyTypeToggleButton
                              value="slabs"
                              label="DA-Balken"
                              selected={visibilityByType.roof_slab_beams}
                              onToggle={() => toggleTypeVisibility('roof_slab_beams')}
                              dataCy="btn-roof-slab-beams-visibility"
                            />
                            <StoreyTypeToggleButton
                              value="purlins"
                              label={PurlinLabel}
                              selected={visibilityByType.purlins}
                              onToggle={() => toggleTypeVisibility('purlins')}
                              onSelect={event =>
                                selectElementsByTypeAndStorey('purlins', roofStoreyKey, {
                                  extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                })
                              }
                              dataCy="btn-purlins-visibility"
                            />
                          </>
                        )}
                      </>
                    }
                  />
                  {reverse(
                    availableNonRoofStoreys.map(s => (
                      <StoreyControlsTableRow
                        key={`${s}-${storeyRowsRenderVersion}`}
                        compact={modelColumnCompact}
                        icon={
                          visibleStoreys.has(s) ? (
                            <VisibilityIcon
                              fontSize="small"
                              sx={{ cursor: 'pointer', color: theme.palette.grey[700] }}
                              onClick={() => toggleSingleStoreyVisibility(s)}
                              data-cy={`btn-storey-${s}-visibility`}
                            />
                          ) : (
                            <VisibilityOffIcon
                              fontSize="small"
                              sx={{ cursor: 'pointer', color: theme.palette.grey[700] }}
                              onClick={() => toggleSingleStoreyVisibility(s)}
                              data-cy={`btn-storey-${s}-visibility`}
                            />
                          )
                        }
                        text={
                          <Typography fontSize="small" noWrap>
                            {storeyToLabel(s)}
                          </Typography>
                        }
                        actionsColumn1={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={event => openStoreyWallColorMenu(event, s)}
                              sx={{
                                padding: 0,
                                color: storeyToSelectedWallColor[s] || theme.palette.grey[700],
                              }}
                              data-cy={`btn-storey-${s}-wall-color-menu`}
                            >
                              <PaletteIcon fontSize="small" />
                            </IconButton>

                            <Checkbox
                              checked={storey === s}
                              onChange={() => setStorey(s)}
                              disabled={!isOrthographic}
                              size="small"
                              sx={{ padding: 0 }}
                            />
                            <TooltipIconButton
                              onClick={() => {
                                if (isOrthographic) {
                                  // A refused storey switch must not change the visibility either
                                  if (storey !== s && refusePaneChange()) return
                                  setIsOrthographic(true)
                                  setStorey(s)
                                  setVisibleStoreys([s])
                                } else {
                                  setVisibleStoreys([s])
                                }
                              }}
                              size="small"
                              sx={{
                                padding: 0,
                              }}
                              tooltip="Aktives SW und Filter wechseln"
                            >
                              <CenterFocusStrongIcon fontSize="small" />
                            </TooltipIconButton>
                            <IconButton
                              data-cy={`btn-storey-${s}-2d-toggle`}
                              onClick={() => {
                                // A refused storey switch must not change the visibility either
                                if (refusePaneChange()) return

                                if (isOrthographic) {
                                  setStorey(null)
                                  setIsOrthographic(false)
                                  setVisibleStoreys([...availableNonRoofStoreys, roofStoreyKey])
                                } else {
                                  setVisibleStoreys([s])
                                  setStorey(s)
                                  setIsOrthographic(true)
                                }
                              }}
                              size="small"
                              sx={{
                                padding: 0,
                              }}
                            >
                              <Typography>{isOrthographic ? '3D' : '2D'}</Typography>
                            </IconButton>
                          </Stack>
                        }
                        visibilityColumn1={
                          <>
                            {!modelColumnCompact && (
                              <StoreyTypeToggleButton
                                value="vertical_slabs"
                                label={DEPosLabel}
                                selected={isTypeVisibleForStorey('vertical_slabs', s)}
                                onToggle={() => toggleTypeVisibilityByStorey('vertical_slabs', s)}
                                onSelect={
                                  canSelectByStorey('vertical_slabs')
                                    ? event =>
                                        selectElementsByTypeAndStorey('vertical_slabs', s, {
                                          extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                        })
                                    : undefined
                                }
                              />
                            )}
                            <StoreyTypeToggleButton
                              value="outer_walls"
                              label={AWLabel}
                              selected={isTypeVisibleForStorey('outer_walls', s)}
                              onToggle={() => toggleTypeVisibilityByStorey('outer_walls', s)}
                              onSelect={
                                canSelectByStorey('outer_walls')
                                  ? event =>
                                      selectElementsByTypeAndStorey('outer_walls', s, {
                                        extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                      })
                                  : undefined
                              }
                            />
                            <StoreyTypeToggleButton
                              value="inner_walls"
                              label={IWLabel}
                              selected={isTypeVisibleForStorey('inner_walls', s)}
                              onToggle={() => toggleTypeVisibilityByStorey('inner_walls', s)}
                              onSelect={
                                canSelectByStorey('inner_walls')
                                  ? event =>
                                      selectElementsByTypeAndStorey('inner_walls', s, {
                                        extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                      })
                                  : undefined
                              }
                            />
                          </>
                        }
                        visibilityColumn2={
                          <>
                            <StoreyTypeToggleButton
                              value="beams"
                              label={BeamLabel}
                              selected={isTypeVisibleForStorey('beams', s)}
                              onToggle={() => toggleTypeVisibilityByStorey('beams', s)}
                              onSelect={
                                canSelectByStorey('beams')
                                  ? event =>
                                      selectElementsByTypeAndStorey('beams', s, {
                                        extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                      })
                                  : undefined
                              }
                            />
                            <StoreyTypeToggleButton
                              value="columns"
                              label={ColumnLabel}
                              selected={isTypeVisibleForStorey('columns', s)}
                              onToggle={() => toggleTypeVisibilityByStorey('columns', s)}
                              onSelect={
                                canSelectByStorey('columns')
                                  ? event =>
                                      selectElementsByTypeAndStorey('columns', s, {
                                        extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                      })
                                  : undefined
                              }
                            />
                          </>
                        }
                        visibilityColumn3={
                          <>
                            <StoreyTypeToggleButton
                              value="lintels"
                              label={LintelLabel}
                              selected={isTypeVisibleForStorey('lintels', s)}
                              onToggle={() => toggleTypeVisibilityByStorey('lintels', s)}
                              onSelect={event =>
                                selectElementsByTypeAndStorey('lintels', s, {
                                  extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                })
                              }
                            />
                            <StoreyTypeToggleButton
                              value="rips"
                              label={RipLabel}
                              selected={isTypeVisibleForStorey('rips', s)}
                              onToggle={() => toggleTypeVisibilityByStorey('rips', s)}
                              onSelect={event =>
                                selectElementsByTypeAndStorey('rips', s, {
                                  extend: eventMatchesPointerModifier(event, 'multiSelect'),
                                })
                              }
                            />
                          </>
                        }
                        additionalColumn1={
                          <Box sx={{ '& .MuiTypography-root': { display: 'none' } }}>
                            <StoreyImageUpload
                              storey={s}
                              onUpload={handleImageUpload}
                              onRemove={handleImageRemove}
                              onUpdateTransform={handleTransformUpdate}
                              onToggleVisibility2D={toggleStoreyImageVisibilityIn2D}
                              onToggleVisibility3D={toggleStoreyImageVisibilityIn3D}
                              hasImage={!!storeyImages[s]}
                              isVisible2D={storeyImages[s]?.isVisible2D ?? true}
                              isVisible3D={storeyImages[s]?.isVisible3D ?? false}
                              isOrthographic={isOrthographic}
                              transform={storeyImages[s] || DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG}
                              isUploading={isUploadingStoreyImage}
                              isRemoving={isRemovingStoreyImage}
                            />
                          </Box>
                        }
                      />
                    )),
                  )}
                  <StoreyControlsTableRow
                    withTopDivider
                    compact={modelColumnCompact}
                    text={
                      <Typography fontSize="small" noWrap>
                        {'Alle SW'}
                      </Typography>
                    }
                    visibilityColumn1={
                      <>
                        {!modelColumnCompact && (
                          <StoreyTypeToggleButton
                            value="vertical_slabs"
                            label="DE-Pos"
                            selected={visibilityByType.vertical_slabs}
                            onToggle={() => toggleTypeVisibility('vertical_slabs')}
                            onSelect={event =>
                              selectElementsByType('vertical_slabs', {
                                extend: eventMatchesPointerModifier(event, 'multiSelect'),
                              })
                            }
                            dataCy="btn-vertical-slabs-visibility"
                          />
                        )}
                        <StoreyTypeToggleButton
                          value="outer_walls"
                          label={AWLabel}
                          selected={visibilityByType.outer_walls}
                          onToggle={() => toggleTypeVisibility('outer_walls')}
                          onSelect={event =>
                            selectElementsByType('outer_walls', {
                              extend: eventMatchesPointerModifier(event, 'multiSelect'),
                            })
                          }
                          dataCy="btn-outer-walls-visibility"
                        />
                        <StoreyTypeToggleButton
                          value="inner_walls"
                          label={IWLabel}
                          selected={visibilityByType.inner_walls}
                          onToggle={() => toggleTypeVisibility('inner_walls')}
                          onSelect={event =>
                            selectElementsByType('inner_walls', {
                              extend: eventMatchesPointerModifier(event, 'multiSelect'),
                            })
                          }
                          dataCy="btn-inner-walls-visibility"
                        />
                      </>
                    }
                    visibilityColumn2={
                      <>
                        <StoreyTypeToggleButton
                          value="beams"
                          label={BeamLabel}
                          selected={visibilityByType.beams}
                          onToggle={() => toggleTypeVisibility('beams')}
                          onSelect={event =>
                            selectElementsByType('beams', {
                              extend: eventMatchesPointerModifier(event, 'multiSelect'),
                            })
                          }
                          dataCy="btn-beams-visibility"
                        />
                        <StoreyTypeToggleButton
                          value="columns"
                          label={ColumnLabel}
                          selected={visibilityByType.columns}
                          onToggle={() => toggleTypeVisibility('columns')}
                          onSelect={event =>
                            selectElementsByType('columns', {
                              extend: eventMatchesPointerModifier(event, 'multiSelect'),
                            })
                          }
                          dataCy="btn-columns-visibility"
                        />
                      </>
                    }
                    visibilityColumn3={
                      <>
                        <StoreyTypeToggleButton
                          value="lintels"
                          label={LintelLabel}
                          selected={visibilityByType.lintels}
                          onToggle={() => toggleTypeVisibility('lintels')}
                          onSelect={event =>
                            selectElementsByType('lintels', {
                              extend: eventMatchesPointerModifier(event, 'multiSelect'),
                            })
                          }
                          dataCy="btn-lintels-visibility"
                        />
                        <StoreyTypeToggleButton
                          value="rips"
                          label={RipLabel}
                          selected={visibilityByType.rips}
                          onToggle={() => toggleTypeVisibility('rips')}
                          onSelect={event =>
                            selectElementsByType('rips', {
                              extend: eventMatchesPointerModifier(event, 'multiSelect'),
                            })
                          }
                          dataCy="btn-rips-visibility"
                        />
                      </>
                    }
                  />
                  {/* Second "Alle SW" line, continuing the row above with the slab/opening
                      toggles moved over from the "Sonstiges" column. */}
                  <StoreyControlsTableRow
                    compact={modelColumnCompact}
                    columnsCombined={
                      <>
                        <StoreyTypeToggleButton
                          value="slabs"
                          label="DE-Fläche"
                          selected={visibilityByType.slabs}
                          onToggle={() => toggleTypeVisibility('slabs')}
                          dataCy="btn-slabs-visibility"
                        />
                        {!modelColumnCompact && (
                          <StoreyTypeToggleButton
                            value="slabs"
                            label="DE-Balken"
                            selected={visibilityByType.slab_beams}
                            onToggle={() => toggleTypeVisibility('slab_beams')}
                            dataCy="btn-slab-beams-visibility"
                          />
                        )}
                        <StoreyTypeToggleButton
                          value="openings"
                          label="Öffnungen"
                          selected={showWallOpenings}
                          onToggle={() => setShowWallOpenings(!showWallOpenings)}
                          dataCy="btn-wall-openings-visibility"
                        />
                        {!modelColumnCompact && (
                          <StoreyTypeToggleButton
                            value="foundation"
                            label="Fundament"
                            selected={visibilityByType.foundation}
                            onToggle={() => toggleTypeVisibility('foundation')}
                            dataCy="btn-foundation-visibility"
                          />
                        )}
                      </>
                    }
                  />
                </StoreyControlsTable>
              </Box>
              {/* "IFC" column: per-storey IFC element-type visibility, mirroring the planar-model
                  storey table. Collapses (no border) when there is no IFC file. Padding lives on the
                  rows inside IFCStoreyControls (not here) so the row-separating divider can bleed to
                  both column borders. */}
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  '&:not(:empty)': {
                    borderLeft: 1,
                    borderColor: 'divider',
                  },
                }}
              >
                <IFCStoreyControls />
              </Box>
              {/* "Sonstiges" column: model/load-type visibility toggles, in their own column. */}
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  borderLeft: 1,
                  borderColor: 'divider',
                  pl: 1,
                  pr: 0.75,
                  py: 0.5,
                  width: 200,
                }}
              >
                <Typography fontSize="small">Sonstiges</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                  <StoreyTypeToggleButton
                    value="stiffening"
                    label={StiffeningLabel}
                    selected={visibility.stiffening}
                    onToggle={() => changeVisibility('stiffening', !visibility.stiffening)}
                    dataCy="btn-stiffening-visibility"
                  />
                  <StoreyTypeToggleButton
                    value="opening-alignment-helper"
                    label="Ausrichtungshilfe"
                    selected={showVerticalOpeningAlignmentHelpers}
                    onToggle={() =>
                      setShowVerticalOpeningAlignmentHelpers(!showVerticalOpeningAlignmentHelpers)
                    }
                    dataCy="btn-opening-alignment-helper-visibility"
                  />
                  <StoreyTypeToggleButton
                    value="pointLoads"
                    label="Punktl."
                    selected={visibility.pointLoads}
                    onToggle={() => changeVisibility('pointLoads', !visibility.pointLoads)}
                  />
                  <StoreyTypeToggleButton
                    value="lineLoads"
                    label="Linienl."
                    selected={visibility.lineLoads}
                    onToggle={() => changeVisibility('lineLoads', !visibility.lineLoads)}
                  />
                  <StoreyTypeToggleButton
                    value="areaLoads"
                    label="Flächenl."
                    selected={visibility.areaLoads}
                    onToggle={() => changeVisibility('areaLoads', !visibility.areaLoads)}
                  />
                  <StoreyTypeToggleButton
                    value="weiteres-filter"
                    label={`Weiteres (${activeExtraFilterCount})`}
                    selected={activeExtraFilterCount > 0}
                    onToggle={toggleAllExtraFilters}
                    onClickSettings={event => setWeiteresFilterAnchorEl(event.currentTarget)}
                    settingsIcon="filter"
                    settingsTooltip="Weitere Filter unter sonstiges"
                    dataCy="btn-weiteres-visibility"
                    settingsDataCy="btn-weiteres-filter"
                  />
                </Box>
              </Box>
              {/* "Plan" column: comment / plan-table / plan-image visibility
                  toggles and the plan filter, in its own column to the right of
                  the storey filters (mirrors the portaled "Bauteil" load-type
                  column next to it). */}
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  borderLeft: 1,
                  borderColor: 'divider',
                  pl: 1,
                  pr: 0.75,
                  py: 0.5,
                }}
              >
                <CommentPlanFilterSelect />
              </Box>
              <Box
                ref={portalRef}
                sx={{
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  '&:not(:empty)': {
                    borderLeft: 1,
                    borderColor: 'divider',
                    pl: 1,
                    pr: 0.75,
                    py: 0.5,
                  },
                }}
              />
            </Box>
          </Box>
          <Menu
            anchorEl={storeyWallColorAnchorEl}
            open={isStoreyWallColorMenuOpen}
            onClose={closeStoreyWallColorMenu}
          >
            {storeyWallColorOptions.map(option => (
              <MenuItem
                key={option.label}
                onClick={() => handleStoreyWallColorChange(option.value)}
                selected={
                  storeyWithColorMenu !== null &&
                  walls
                    .filter(wall => wall.storey === storeyWithColorMenu)
                    .every(wall => manualGuidToColor[wall.guid] === option.value)
                }
              >
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'grey.400',
                    mr: 1,
                    bgcolor: option.value || 'transparent',
                  }}
                />
                {option.label}
              </MenuItem>
            ))}
            <Divider />
            {storeyWallLinewidthOptions.map(option => (
              <MenuItem
                key={option.label}
                onClick={() => handleStoreyWallLinewidthChange(option.value)}
                selected={
                  storeyWithColorMenu !== null &&
                  storeyToWallLinewidth[storeyWithColorMenu] === option.value
                }
              >
                Linienbreite {option.label}
              </MenuItem>
            ))}
          </Menu>
          <ModelSettingsPopover
            anchorEl={modelSettingsAnchorEl}
            onClose={() => setModelSettingsAnchorEl(null)}
          />
          <WeiteresFilterPopover
            anchorEl={weiteresFilterAnchorEl}
            onClose={() => setWeiteresFilterAnchorEl(null)}
          />
        </CollapsibleVerticalDivider>
      </Box>
      <Box
        ref={snackbarAnchorRef}
        sx={{
          position: 'absolute',
          top: '100%',
          right: 0,
          width: '100%',
          minWidth: 288,
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 1400,
          '& .notistack-SnackbarContainer': {
            position: 'absolute',
            top: 0,
            right: 0,
          },
          marginRight: 2,
          marginTop: 1,
        }}
      />
    </Box>
  )
}

export default StoreyControls
